import { audioCtx } from "./context.js";
import { globalSfxVolume } from "./sfx.js";

/* ---- Modern 4K Sci-Fi Synthesizers ---- */
export function playSynthPewModern(volume: number, rate = 1.0) {
  if (audioCtx.state === "suspended") audioCtx.resume();
  const now = audioCtx.currentTime;
  const duration = 0.18 / rate;
  const osc = audioCtx.createOscillator();
  osc.type = "sawtooth"; // futuristic energy discharge
  osc.frequency.setValueAtTime(1400 * rate, now);
  osc.frequency.exponentialRampToValueAtTime(100 * rate, now + duration);
  const gain = audioCtx.createGain();
  gain.gain.setValueAtTime(0.22 * volume * globalSfxVolume, now);
  gain.gain.exponentialRampToValueAtTime(0.001, now + duration);
  osc.connect(gain);
  gain.connect(audioCtx.destination);
  osc.start(now);
  osc.stop(now + duration);
}

export function playSynthExplosionModern(volume: number) {
  if (audioCtx.state === "suspended") audioCtx.resume();
  const now = audioCtx.currentTime;
  const duration = 0.5;

  // 1. Deep Sub Bass drop
  const osc = audioCtx.createOscillator();
  osc.type = "sine";
  osc.frequency.setValueAtTime(80, now);
  osc.frequency.linearRampToValueAtTime(20, now + duration);
  const oscGain = audioCtx.createGain();
  oscGain.gain.setValueAtTime(0.6 * volume * globalSfxVolume, now);
  oscGain.gain.exponentialRampToValueAtTime(0.001, now + duration);
  osc.connect(oscGain);
  oscGain.connect(audioCtx.destination);
  osc.start(now);
  osc.stop(now + duration);

  // 2. Futuristic noise capacitor blast
  const bufferSize = audioCtx.sampleRate * 0.35;
  const buffer = audioCtx.createBuffer(1, bufferSize, audioCtx.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < bufferSize; i++) {
    data[i] = Math.random() * 2 - 1;
  }
  const noise = audioCtx.createBufferSource();
  noise.buffer = buffer;
  const filter = audioCtx.createBiquadFilter();
  filter.type = "bandpass";
  filter.frequency.setValueAtTime(350, now);
  const noiseGain = audioCtx.createGain();
  noiseGain.gain.setValueAtTime(0.45 * volume * globalSfxVolume, now);
  noiseGain.gain.exponentialRampToValueAtTime(0.001, now + 0.35);
  noise.connect(filter);
  filter.connect(noiseGain);
  noiseGain.connect(audioCtx.destination);
  noise.start(now);
  noise.stop(now + 0.35);
}

export function playSynthPowerupModern(volume: number) {
  if (audioCtx.state === "suspended") audioCtx.resume();
  const now = audioCtx.currentTime;
  const duration = 0.45;
  const osc1 = audioCtx.createOscillator();
  const osc2 = audioCtx.createOscillator();
  osc1.type = "sine";
  osc2.type = "triangle";
  osc1.frequency.setValueAtTime(320, now);
  osc1.frequency.exponentialRampToValueAtTime(960, now + duration);
  osc2.frequency.setValueAtTime(160, now);
  osc2.frequency.exponentialRampToValueAtTime(480, now + duration);
  const gain = audioCtx.createGain();
  gain.gain.setValueAtTime(0.18 * volume * globalSfxVolume, now);
  gain.gain.exponentialRampToValueAtTime(0.001, now + duration);
  osc1.connect(gain);
  osc2.connect(gain);
  gain.connect(audioCtx.destination);
  osc1.start(now);
  osc2.start(now);
  osc1.stop(now + duration);
  osc2.stop(now + duration);
}

export function playSynthOofModern(volume: number) {
  if (audioCtx.state === "suspended") audioCtx.resume();
  const now = audioCtx.currentTime;
  const duration = 0.12;
  const osc = audioCtx.createOscillator();
  osc.type = "sine";
  osc.frequency.setValueAtTime(450, now);
  osc.frequency.exponentialRampToValueAtTime(220, now + duration);
  const gain = audioCtx.createGain();
  gain.gain.setValueAtTime(0.25 * volume * globalSfxVolume, now);
  gain.gain.exponentialRampToValueAtTime(0.001, now + duration);
  osc.connect(gain);
  gain.connect(audioCtx.destination);
  osc.start(now);
  osc.stop(now + duration);
}

export function playSynthReloadingModern(volume: number) {
  if (audioCtx.state === "suspended") audioCtx.resume();
  const now = audioCtx.currentTime;
  const duration = 0.15;
  const osc = audioCtx.createOscillator();
  osc.type = "sine";
  osc.frequency.setValueAtTime(240, now);
  osc.frequency.exponentialRampToValueAtTime(640, now + duration);
  const gain = audioCtx.createGain();
  gain.gain.setValueAtTime(0.1 * volume * globalSfxVolume, now);
  gain.gain.exponentialRampToValueAtTime(0.001, now + duration);
  osc.connect(gain);
  gain.connect(audioCtx.destination);
  osc.start(now);
  osc.stop(now + duration);
}

export function playSynthWinModern(volume: number) {
  if (audioCtx.state === "suspended") audioCtx.resume();
  const now = audioCtx.currentTime;
  const notes = [293.66, 349.23, 440.00, 523.25, 587.33, 698.46, 880.00]; // Dm9 chord sweep
  notes.forEach((freq, idx) => {
    const osc = audioCtx.createOscillator();
    osc.type = "sine";
    osc.frequency.setValueAtTime(freq, now + idx * 0.08);
    const gain = audioCtx.createGain();
    gain.gain.setValueAtTime(0.15 * volume * globalSfxVolume, now + idx * 0.08);
    gain.gain.exponentialRampToValueAtTime(0.001, now + 0.6);
    osc.connect(gain);
    gain.connect(audioCtx.destination);
    osc.start(now + idx * 0.08);
    osc.stop(now + 0.6);
  });
}

export function playSynthLoseModern(volume: number) {
  if (audioCtx.state === "suspended") audioCtx.resume();
  const now = audioCtx.currentTime;
  const osc = audioCtx.createOscillator();
  osc.type = "sawtooth";
  osc.frequency.setValueAtTime(90, now);
  osc.frequency.exponentialRampToValueAtTime(30, now + 1.2);
  const gain = audioCtx.createGain();
  gain.gain.setValueAtTime(0.35 * volume * globalSfxVolume, now);
  gain.gain.exponentialRampToValueAtTime(0.001, now + 1.2);
  osc.connect(gain);
  gain.connect(audioCtx.destination);
  osc.start(now);
  osc.stop(now + 1.2);
}
